import React from 'react';
import clsx from 'clsx';
import { is } from '../../services/index';
import { getAlignClassName } from '../../objects/facets/Alignable';
import { getAlertClassName } from '../../objects/facets/Alert';
import { getColorClassName } from '../../objects/facets/Color';
import { ButtonMarkup } from './ButtonEnum';
import { hashClass } from '../../helpers';
import { useTrilogyContext } from '../../context';
import { Icon } from "../icon";
import { VariantState } from "../../objects";
/**
 * Button component
 * @param loading {boolean} Loading button
 * @param inverted {boolean} Inverted button
 * @param disabled {boolean} Disabled button
 * @param variant {ButtonColor} Button color : primary|secondary|info|warning|success|danger. Tertiary is deprecated
 * @param alert {AlertState} Alert variant color for Button
 * @param children {ReactNode} Button child
 * @param fullwidth {boolean} Fullwidth button
 * @param small {boolean} Small button
 * @param align {Alignable} Button alignment
 * @param justify {Justifiable} Button justification
 * @param onClick {Function} Click Event
 * @param accessibilityLabel {string} Accessibility label
 * @param testId {string} Test Id for Test Integration
 * @param iconName {IconName} If Icon, Button + Icon && Button IconName
 * - -------------------------- WEB PROPERTIES -------------------------------
 * @param markup {ButtonMarkup} HTML element : button|input|a (ONLY FOR WEB)
 * @param className {string} Additionnal css classes (ONLY FOR WEB)
 * @param id {string} Custom id for button (ONLY FOR WEB)
 * @param to {string} Link
 * @param href {string} Href
 * @param name {string} Button name attribute
 * @param routerLink Custom Router Link as props
 * @param styled {boolean} Component Wearing Styles - Hashed Trilogy Css
 * - --------------- NATIVE PROPERTIES ----------------------------------
 * @param accessibilityActivate {boolean}
 */
const Button = ({ markup, loading, variant, href, id, fullwidth, children, className, inverted, align, justify, alert, small, to, accessibilityLabel, testId, onClick, name, routerLink, type, iconName, ...others }) => {
    const isDisabled = others.disabled || false;
    const { styled } = useTrilogyContext();
    /** Check if specified markup is valid */
    const isCorrectMarkup = (stringMarkup) => {
        if (stringMarkup in ButtonMarkup || Object.values(ButtonMarkup).includes(stringMarkup))
            return true;
    };
    const getClassNames = (inverted, loading, variant, alert, align, justify, fullwidth, small, className) => {
        return clsx('button', inverted && is('inverted'), loading ? is('loading') : is('loaded'), variant && !alert && is(getColorClassName(variant)), alert && is(getAlertClassName(alert)), align && is(getAlignClassName(align)), justify && is(justify), fullwidth && is('fullwidth'), small && is('small'), className);
    };
    const classes = hashClass(styled, getClassNames(inverted, loading, variant, alert, align, justify, fullwidth, small, className));
    const Tag = markup && isCorrectMarkup(markup) ? markup : 'button';
    if (Tag === 'button') {
        return (React.createElement("button", { id: id, "data-testid": testId, "aria-label": accessibilityLabel, className: classes, disabled: isDisabled, name: name, onClick: (e) => {
                // eslint-disable-next-line no-unused-expressions
                !isDisabled && (onClick === null || onClick === void 0 ? void 0 : onClick(e));
                e.stopPropagation();
            }, type: type !== null && type !== void 0 ? type : 'button', ...others },
            iconName && React.createElement(Icon, { className: !children ? 'is-marginless' : '', color: variant === VariantState.TERTIARY ? 'SECONDARY' : 'WHITE', name: iconName }),
            children));
    }
    if (Tag === 'input') {
        return (React.createElement("input", { id: id, "data-testid": testId, className: classes, "aria-label": accessibilityLabel, name: name, onClick: (e) => {
                // eslint-disable-next-line no-unused-expressions
                !isDisabled && (onClick === null || onClick === void 0 ? void 0 : onClick(e));
                e.stopPropagation();
            }, disabled: isDisabled, type: type !== null && type !== void 0 ? type : 'submit', value: `${children}`, ...others }));
    }
    if (routerLink && to && !isDisabled) {
        const RouterLink = (routerLink ? routerLink : 'a');
        return (React.createElement(RouterLink, { "aria-label": accessibilityLabel, "data-testid": testId, to: to, className: classes, ...others },
            iconName && React.createElement(Icon, { className: !children ? 'is-marginless' : '', color: variant === VariantState.TERTIARY ? 'SECONDARY' : 'WHITE', name: iconName }),
            children));
    }
    return (React.createElement("a", { id: id, "data-testid": testId, "aria-label": accessibilityLabel, className: classes, href: href, onClick: (e) => {
            // eslint-disable-next-line no-unused-expressions
            !isDisabled && (onClick === null || onClick === void 0 ? void 0 : onClick(e));
            e.stopPropagation();
        }, ...others },
        iconName && React.createElement(Icon, { className: !children ? 'is-marginless' : '', color: variant === VariantState.TERTIARY ? 'SECONDARY' : 'WHITE', name: iconName }),
        children));
};
export default Button;
//# sourceMappingURL=Button.js.map